import { useUpdateEffect } from 'ahooks';
import { useCallback, useEffect, useRef } from 'react';
import type { IPdfReaderState } from '../types';
import { resetAllScrollPositions } from '../utils/scrollUtils';
import { usePdfLayout } from './usePdfLayout';
import useSwipe from './useSwipe';

export interface UsePdfViewportLogicOptions {
    state: IPdfReaderState;
    // biome-ignore lint/suspicious/noExplicitAny: controller is a loosely-typed legacy object
    controller: any;
    containerRef: React.RefObject<HTMLDivElement>;
}

export interface UsePdfViewportLogicReturn {
    // Refs
    wrapperRef: React.RefObject<HTMLDivElement>;
    horizontalRef: React.RefObject<HTMLDivElement>;
    // States
    visibleRange: { start: number; end: number };
    shouldShowAllPages: boolean;
    pageHeights: number[]; // 兼容旧接口，实际为空
    prefixHeights: number[]; // 兼容旧接口，实际为空
    totalContentHeight: number;
    // Handlers
    handleContentClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    // V2 Layout API
    updatePageHeight: (index: number, height: number) => void;
    getPageTop: (index: number) => number;
    getPageHeight: (index: number) => number;
    scrollToPage: (index: number, behavior?: ScrollBehavior) => void;
}

/**
 * PDF 视口业务逻辑 Hook (已集成 V2 Layout 引擎)
 */
export function usePdfViewportLogic(options: UsePdfViewportLogicOptions): UsePdfViewportLogicReturn {
    const { state, controller, containerRef } = options;
    const { totalPages, pdfDoc, scale, pageFlipMode, pageNum, screenState } = state;
    const { nextPage, prevPage, toggleToolbar, setPageNumSilent } = controller;

    // ========== 渲染模式判断 ==========
    const shouldShowAllPages = screenState === 'full' && pageFlipMode === 'vertical';

    // ========== V2 Layout 引擎集成 ==========
    const layout = usePdfLayout({
        pdfDoc,
        totalPages,
        scale: scale || 1,
        enabled: shouldShowAllPages,
        containerRef,
        initialPageNum: pageNum?.value, // 传入初始页码，用于模式切换后的自动定位
    });

    const {
        visibleRange,
        totalHeight: totalContentHeight,
        centerPageIndex,
        isAtTop,
        isAtBottom,
        updatePageHeight,
        scrollToPage,
        getPageTop,
        getPageHeight,
    } = layout;

    const wrapperRef = useRef<HTMLDivElement>(null);
    const horizontalRef = useRef<HTMLDivElement>(null);
    const isInternalPageUpdateRef = useRef(false);
    // 用 ref 跟踪 shouldShowAllPages 的最新值，避免 useSwipe onSwipeEnd 闭包读到陈旧值
    const shouldShowAllPagesRef = useRef(shouldShowAllPages);
    shouldShowAllPagesRef.current = shouldShowAllPages;
    // 版本号机制：替代 setTimeout 防止 scroll->page 触发反向 scrollToPage
    // silentVersionRef 在每次 setPageNumSilent 前递增；
    // processedSilentVersionRef 在 Page->Scroll effect 中消费，若未消费则说明该次 pageNum 变化来自内部滚动同步。
    const silentVersionRef = useRef(0);
    const processedSilentVersionRef = useRef(0);
    /**
     * Jump Transaction：
     * - requestedPage: 用户显式跳转目标（目录/工具栏/搜索）
     * - deadline: 兜底超时，避免极端情况下事务永不结束导致页码不再随滚动更新
     * - stableHits: 当主导可视页连续命中 requestedPage 若干次后，认为跳转完成并解锁
     */
    const jumpTxnRef = useRef<null | { requestedPage: number; deadline: number; stableHits: number }>(null);
    const lastPageNumValueRef = useRef<number | null>(null);
    const lastShouldShowAllPagesRef = useRef<boolean>(false);

    const clampIndex = (idx: number, total: number) => {
        if (!Number.isFinite(idx)) {
            return 0;
        }
        if (total <= 0) {
            return 0;
        }
        if (idx < 0) return 0;
        if (idx >= total) return total - 1;
        return idx;
    };

    const startJumpTxn = useCallback(
        (requestedPage: number) => {
            const safe = Number(requestedPage);
            if (!Number.isFinite(safe) || safe < 1 || safe > totalPages) {
                jumpTxnRef.current = null;
                return;
            }
            jumpTxnRef.current = {
                requestedPage: safe,
                deadline: Date.now() + 4000,
                stableHits: 0,
            };
        },
        [totalPages]
    );

    const endJumpTxnIfNeeded = useCallback((dominantPage: number) => {
        const txn = jumpTxnRef.current;
        if (!txn) {
            return;
        }
        const now = Date.now();
        if (now > txn.deadline) {
            jumpTxnRef.current = null;
            return;
        }
        if (dominantPage === txn.requestedPage) {
            txn.stableHits += 1;
            if (txn.stableHits >= 2) {
                jumpTxnRef.current = null;
            }
            return;
        }
        txn.stableHits = 0;
    }, []);

    const getDominantVisiblePageNum = useCallback((): number | null => {
        if (!shouldShowAllPages) {
            return null;
        }
        if (totalPages <= 0) {
            return null;
        }
        const container = containerRef.current;
        if (!container) {
            return null;
        }

        // 边界吸附：到顶/到底时强制为首/末页，解决“到底仍显示倒数第二页”
        if (isAtTop) {
            return 1;
        }
        if (isAtBottom) {
            return totalPages;
        }

        const viewportTop = container.scrollTop;
        const viewportBottom = viewportTop + container.clientHeight;
        let bestIndex = clampIndex(centerPageIndex, totalPages);
        let bestVisible = -1;

        // 只在可见范围内计算主导可视页（range 自带 overscan，不会漏掉）
        const start = Math.max(0, Math.min(totalPages - 1, visibleRange.start));
        const end = Math.max(start + 1, Math.min(totalPages, visibleRange.end));

        for (let i = start; i < end; i++) {
            const top = getPageTop(i);
            const height = getPageHeight(i);
            if (!(Number.isFinite(top) && Number.isFinite(height)) || height <= 0) {
                continue;
            }
            const bottom = top + height;
            const visible = Math.min(bottom, viewportBottom) - Math.max(top, viewportTop);
            if (visible <= 0) {
                continue;
            }
            if (visible > bestVisible || (visible === bestVisible && i < bestIndex)) {
                bestVisible = visible;
                bestIndex = i;
            }
        }

        return bestIndex + 1;
    }, [
        shouldShowAllPages,
        totalPages,
        containerRef,
        isAtTop,
        isAtBottom,
        centerPageIndex,
        visibleRange.start,
        visibleRange.end,
        getPageTop,
        getPageHeight,
    ]);

    // 进入“全屏 + 垂直滚动”模式时，立即以当前页开启 Jump Transaction，避免 scroll->page 把页码覆盖成 1
    useEffect(() => {
        const prev = lastShouldShowAllPagesRef.current;
        lastShouldShowAllPagesRef.current = shouldShowAllPages;
        if (!shouldShowAllPages || prev === shouldShowAllPages) {
            return;
        }
        const current = pageNum?.value;
        if (typeof current === 'number' && current >= 1 && current <= totalPages) {
            startJumpTxn(current);
        }
    }, [shouldShowAllPages, pageNum?.value, totalPages, startJumpTxn]);

    // ========== 内容点击处理 ==========
    const handleContentClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (screenState !== 'full') return;
            const target = e.target as HTMLElement;
            const selection = window.getSelection();
            if (
                target.tagName === 'A' ||
                target.tagName === 'BUTTON' ||
                target.closest('a') ||
                target.closest('button') ||
                target.closest('.umu-pdf-version-toolbar-container') ||
                (selection && selection.toString().length > 0)
            )
                return;
            toggleToolbar();
        },
        [screenState, toggleToolbar]
    );

    // ========== 页码同步 (从 滚动 到 页码) ==========
    useUpdateEffect(() => {
        if (!(shouldShowAllPages && setPageNumSilent)) return;

        // 每次 effect 执行时立即消费 isInternalPageUpdateRef（同步重置，替代 setTimeout）
        const wasInternal = isInternalPageUpdateRef.current;
        isInternalPageUpdateRef.current = false;

        // 关键：如果本次 render 的 pageNum 发生了”外部变化”（目录/工具栏/搜索/模式切换带来的目标页），
        // 必须先开启 Jump Transaction，避免该变化在 effect 顺序上被 scroll->page 反向覆盖。
        const currentPageNum = pageNum?.value;
        const lastPageNum = lastPageNumValueRef.current;
        lastPageNumValueRef.current = typeof currentPageNum === 'number' ? currentPageNum : null;
        const isExternalPageChange =
            typeof currentPageNum === 'number' &&
            typeof lastPageNum === 'number' &&
            currentPageNum !== lastPageNum &&
            !wasInternal; // 使用已消费的值，而非实时值
        if (isExternalPageChange) {
            startJumpTxn(currentPageNum);
            return;
        }

        const nextNum = getDominantVisiblePageNum() ?? centerPageIndex + 1;

        // Jump Transaction 期间不允许 scroll->page 覆盖用户目标页；但我们仍用主导可视页来判断是否已稳定到达目标，从而提前解锁
        if (jumpTxnRef.current) {
            endJumpTxnIfNeeded(nextNum);
            return;
        }

        if (nextNum !== pageNum?.value && nextNum >= 1 && nextNum <= totalPages) {
            isInternalPageUpdateRef.current = true; // 保留：供下次本 effect 执行时的 wasInternal 消费
            silentVersionRef.current += 1; // 递增版本号，Page->Scroll effect 据此判断是否跳过 scrollToPage
            setPageNumSilent(nextNum);
            // 不再使用 setTimeout 重置，isInternalPageUpdateRef 将在下次本 effect 执行时同步消费
        }
    }, [
        centerPageIndex,
        getDominantVisiblePageNum,
        endJumpTxnIfNeeded,
        totalPages,
        pageNum?.value,
        shouldShowAllPages,
        setPageNumSilent,
        startJumpTxn,
    ]);

    // ========== 搜索可见页同步 (用于高亮范围锁定) ==========
    useEffect(() => {
        const updateVisiblePages = controller?.updateVisiblePages as undefined | ((pages: number[]) => void);
        if (!(updateVisiblePages && pdfDoc)) return;

        const pages: number[] = [];
        if (shouldShowAllPages) {
            for (let i = visibleRange.start; i < visibleRange.end; i++) {
                pages.push(i + 1);
            }
        } else if (pageNum?.value > 0) {
            pages.push(pageNum.value);
        }
        updateVisiblePages(pages);
    }, [visibleRange.start, visibleRange.end, pageNum?.value, shouldShowAllPages, controller, pdfDoc]);

    // ========== 页面跳转 (从 页码 到 滚动) ==========
    useUpdateEffect(() => {
        if (!shouldShowAllPages) {
            return;
        }

        // 版本号检测：若 pageNum 是由 setPageNumSilent（scroll->page 内部同步）触发的，跳过 scrollToPage，
        // 避免 scroll->page->scroll 循环。使用 ref 同步操作，无 setTimeout 竞争风险。
        if (processedSilentVersionRef.current < silentVersionRef.current) {
            processedSilentVersionRef.current = silentVersionRef.current; // 消费版本
            return;
        }

        const targetPage = pageNum?.value;
        if (targetPage >= 1 && targetPage <= totalPages) {
            // 用户显式跳转：无论跨度大小都开启 Jump Transaction，避免短文档多页同屏时被”中心页校准”覆盖
            startJumpTxn(targetPage);
            // 搜索跳转通常需要较快的响应，使用 smooth 增加过渡感
            scrollToPage(targetPage - 1, 'smooth');
        }
    }, [pageNum?.value, totalPages, shouldShowAllPages, startJumpTxn, scrollToPage]);

    // ========== 模式切换处理 ==========
    useEffect(() => {
        if (containerRef.current) {
            if (shouldShowAllPages) {
                containerRef.current.classList.remove('horizontal-mode');
            } else if (screenState === 'full') {
                containerRef.current.classList.add('horizontal-mode');
                resetAllScrollPositions(containerRef.current);
            }
        }
    }, [shouldShowAllPages, screenState]);

    // ========== 水平模式手势支持 ==========
    useSwipe({
        swipeRef: horizontalRef,
        // 放大后容器出现横向滚动条时，滑动为滚动内容而非翻页，scrollableRef 负责检测该状态
        scrollableRef: horizontalRef,
        onSwipeEnd: (direction) => {
            // 全屏垂直模式通过竖向滚动翻页，禁用左右滑动切换页面。
            // 使用 ref 读取最新值，防止 onSwipeEnd 闭包因 useSwipe effect 延迟重建而捕获陈旧值。
            if (shouldShowAllPagesRef.current) return;
            if (direction === 'left') nextPage();
            else if (direction === 'right') prevPage();
        },
    });

    return {
        wrapperRef,
        horizontalRef,
        visibleRange,
        shouldShowAllPages,
        pageHeights: [], // 兼容旧接口
        prefixHeights: [], // 兼容旧接口
        totalContentHeight,
        handleContentClick,
        updatePageHeight,
        getPageTop,
        getPageHeight,
        scrollToPage,
    };
}
